/**
 * useExpenseConflicts — Hook for the Conflict Resolver UI
 * ───────────────────────────────────────────────────────
 * Reads `gastos_embarque` rows tagged with a `grupo_conflicto_id` (server-side
 * detection via the `detect_gasto_conflicts` RPC + trigger) and groups them
 * so the UI can render an amber banner with a RadioGroup per conflict.
 *
 * Resolution = the user picks ONE gasto per group as authoritative; the rest
 * are flipped to `aplica_costeo_inventario = false` so the engine ignores
 * them. This avoids double-counting (e.g., flete from BL vs. Casilla 64 of
 * the DIM) without deleting historical data.
 */
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ConflictGasto {
  id: string;
  concepto: string;
  monto: number;
  moneda: string;
  fuente: string | null;
  categoria_id: string | null;
  confianza_ocr: number | null;
  documento_id: string | null;
  aplica_costeo_inventario: boolean;
  grupo_conflicto_id: string | null;
}

export interface ConflictGroup {
  groupId: string;
  categoria: string | null;
  candidates: ConflictGasto[];
  /** Currently selected gasto (the one with aplica_costeo_inventario = true). */
  selectedId: string | null;
  /** True when more than one candidate is still applied → user MUST choose. */
  unresolved: boolean;
}

/** Fetch all gastos for an embarque grouped by `grupo_conflicto_id`. */
export function useExpenseConflicts(embarqueId: string | null | undefined) {
  const { orgId } = useAuth();

  const query = useQuery({
    queryKey: ["expense-conflicts", orgId, embarqueId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("gastos_embarque")
        .select(
          "id, concepto, monto, moneda, fuente, categoria_id, confianza_ocr, documento_id, aplica_costeo_inventario, grupo_conflicto_id",
        )
        .eq("embarque_id", embarqueId!)
        .eq("org_id", orgId!)
        .not("grupo_conflicto_id", "is", null)
        .is("deleted_at", null);
      if (error) throw error;
      return (data ?? []) as ConflictGasto[];
    },
    enabled: !!orgId && !!embarqueId,
  });

  const groups = useMemo<ConflictGroup[]>(() => {
    const rows = query.data ?? [];
    const map = new Map<string, ConflictGasto[]>();
    for (const row of rows) {
      const key = row.grupo_conflicto_id;
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return Array.from(map.entries())
      .filter(([, list]) => list.length > 1)
      .map(([groupId, list]) => {
        const applied = list.filter((g) => g.aplica_costeo_inventario);
        const selectedId = applied.length === 1 ? applied[0].id : null;
        return {
          groupId,
          categoria: list[0]?.categoria_id ?? null,
          candidates: list.sort((a, b) => (b.confianza_ocr ?? 0) - (a.confianza_ocr ?? 0)),
          selectedId,
          unresolved: applied.length !== 1,
        };
      });
  }, [query.data]);

  const hasUnresolved = groups.some((g) => g.unresolved);

  return {
    ...query,
    groups,
    hasUnresolved,
  };
}

/**
 * Resolve a conflict group: mark `winnerId` as the authoritative gasto
 * and disable proration for all other candidates in the same group.
 */
export function useResolveConflict() {
  const qc = useQueryClient();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      groupId,
      winnerId,
      embarqueId,
    }: {
      groupId: string;
      winnerId: string;
      embarqueId: string;
    }) => {
      if (!orgId) throw new Error("No org context");

      // Disable all gastos in the group …
      const { error: e1 } = await (supabase as any)
        .from("gastos_embarque")
        .update({ aplica_costeo_inventario: false })
        .eq("grupo_conflicto_id", groupId)
        .eq("org_id", orgId);
      if (e1) throw e1;

      // … then re-enable only the winner.
      const { error: e2 } = await (supabase as any)
        .from("gastos_embarque")
        .update({ aplica_costeo_inventario: true })
        .eq("id", winnerId)
        .eq("org_id", orgId);
      if (e2) throw e2;

      return { groupId, winnerId, embarqueId };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["expense-conflicts"] });
      qc.invalidateQueries({ queryKey: ["gastos-embarque"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", res.embarqueId] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}
